// External libraries
import { useState } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import type { QueryResult } from '@apollo/react-common';

// Internal modules
import { logger } from '@/lib/logger';
import { CONSUMER_FLOW_ID_QUERY } from './graphql';
import { getGlobals, getToken } from '@/lib/globals';
import { buildUrl, redirectToConsumer } from '@/lib/utils';
import { CI } from '@/lib/customerInteractions/ci';

type ConsumerAddBankQueryTuple = [() => void, QueryResult];
type ConsumerAddBankRedirectTuple = [() => void, { isRedirecting: boolean }];

const TOKEN = getToken();
const BASE_URI = getGlobals().uri.base;
const DEFAULT_FLOW_ID = '?flow=undefined';

const useLazyConsumerAddBankQuery = (): ConsumerAddBankQueryTuple => {
  const [getFlowId, status] = useLazyQuery(CONSUMER_FLOW_ID_QUERY);
  const getFlowIdWithBoundArgs = () => {
    logger.fpti('prepare_tesla_add_bank', {
      state_name: 'signup',
      transition_name: 'prepare_tesla_bank',
    });

    getFlowId({
      variables: {
        redirectUrl: buildUrl(`${BASE_URI}/webapps/hermes`, {
          token: TOKEN,
        }),
      },
    });
  };

  return [getFlowIdWithBoundArgs, status];
};

export const useConsumerAddBankRedirect = (): ConsumerAddBankRedirectTuple => {
  const [executeQuery, { data, error }] = useLazyConsumerAddBankQuery();
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const triggerRedirect = () => {
    setIsRedirecting(true);
    executeQuery();
  };

  if (data || error) {
    logger.fpti('add_bank_redirect', {
      state_name: 'signup',
      transition_name: 'process_tesla_continue',
    });

    // If the ConsumerAddBankQuery returns an error then `data`
    // will be `undefined` and we use the default flow id.
    const { flowId = DEFAULT_FLOW_ID } = data?.consumerFlowId || {};

    CI.endWith(CI.Event.ADD_BANK_REDIRECT_TO_CONSUMER, CI.Status.Ok);
    redirectToConsumer(flowId, { tsrce: 'checkoutuinodeweb' });
  }

  return [triggerRedirect, { isRedirecting }];
};
